import { FetchModalProductPopUp } from '../../g-modal/g-modal';

interface FoxsellVariant {
  id: number;
  title: string;
  price: number;
  handle?: string;
  available?: boolean;
}

interface FaceVariant {
  id: number;
  index: number;
  title?: string;
  price?: number;
}

interface EngravingProperties {
  [key: string]: string;
}

interface StorageData {
  id: number | string;
  category: string;
  quantity: number;
  title: string;
  image: string;
  price: number;
  type: string;
  handle: string;
  inventory_quantity: number;
  product_type: string;
  properties: EngravingProperties;
}

interface BoundHandlers {
  updateStorage: () => void;
  variantChange: (event: Event) => void;
  checkboxClick: (event: Event) => void;
  validateFields: () => void;
  buttonClick: (event: Event) => void;
}

interface FaceImages {
  image1?: string | null;
  image2?: string | null;
  image3?: string | null;
}

interface CategoryImages {
  face1?: FaceImages;
  face2?: FaceImages;
  face3?: FaceImages;
}

interface CustomSelectElements {
  label: HTMLElement;
  hiddenInput: HTMLInputElement;
  dropdown: HTMLElement;
  arrow: HTMLElement;
}

interface FontOption {
  name: string;
  url?: string | null;
}

// PapaParse library interface
interface PapaParseResult {
  data: Array<{ font_name?: string; font_url?: string }>;
}

interface PapaParseStatic {
  parse: (data: string, options: {
    header: boolean;
    complete: (results: PapaParseResult) => void;
  }) => void;
}

// Extend window interface for global variables
declare global {
  interface Window {
    categoryEngravingImages?: Record<string, CategoryImages>;
    foxsellPriceMap?: Record<string, number>;
    mixMatchStore: {
      setStorageData: (data: StorageData) => void;
    };
    formatMoney?: (price: number, format: string) => string;
    moneyFormat?: string;
    Papa?: PapaParseStatic;
  }
}

// Mix & Match Product Popup with personalization features
export class MMProductPopup extends FetchModalProductPopUp {
  // UI Elements
  addButtonEl: HTMLElement | null = null;
  priceEls: NodeListOf<Element> | null = null;
  mainContent: HTMLElement | null = null;
  variantSwatches: NodeListOf<HTMLInputElement> | null = null;
  faceCheckboxes: NodeListOf<HTMLInputElement> | null = null;
  customSelects: NodeListOf<Element> | null = null;
  productGallery: HTMLElement | null = null;
  colorNameEl: HTMLElement | null = null;
  productTitleEl: HTMLElement | null = null;
  titlePriceEl: HTMLElement | null = null;
  buttonPriceEl: HTMLElement | null = null;

  // Data
  foxsellVariants: FoxsellVariant[] = [];
  shopifyPrices: Record<number | string, number> = {};
  variantId: number | string = '';
  title: string = '';
  originalPrice: number = 0;
  handle: string = '';
  productType: string = '';
  image: string = '';
  inventoryQuantity: number = 0;
  inventoryManagement: string = '';
  inventoryPolicy: string = '';
  category: string = '';

  // Base variant info for reverting
  baseVariantId: number | string = '';
  baseTitle: string = '';
  basePrice: number = 0;
  baseHandle: string = '';

  // Bound handlers
  private _boundHandlers: BoundHandlers | null = null;
  
  // State subscription
  private _unsubscribe: (() => void) | null = null;

  constructor() {
    super();
  }

  close(): void {
    this._checkForRefetch();
    // Call parent close
    super.close();
  }

  private _checkForRefetch(): void {
    if (!this._lastTriggerEl) return;

    const triggerVariantId = this._lastTriggerEl.dataset?.variantId;
    if (!triggerVariantId) return;

    // Get currently checked swatch's variant ID (what the modal is showing)
    let currentModalVariantId: string | null = null;
    if (this.variantSwatches) {
      const checkedSwatch = Array.from(this.variantSwatches).find(s => s.checked);
      if (checkedSwatch) {
        currentModalVariantId = checkedSwatch.dataset.variantId || null;
      }
    }

    // Compare trigger's variant with the modal's current variant
    if (currentModalVariantId && triggerVariantId !== currentModalVariantId) {
      // Modal is showing different product than trigger, force re-fetch on next open
      this.previousVariantId = null;
    }
  }

  async open(productHandle?: string, variantId?: string | null, category: string | null = null): Promise<boolean> {
    // Call parent open to fetch and render content
    const isNewContent = await super.open(productHandle, variantId, category);

    // Only re-initialize if new content was fetched
    if (isNewContent) {
      this._cacheUIElements();
      this._init();
      this._fillData();
      this._injectEngravingImages(category);
    }

    return isNewContent;
  }

  private _injectEngravingImages(category: string | null): void {
    // Get all face image wrappers
    const faceWrappers = this.content?.querySelectorAll('.js-engraving-images-wrapper') || [];
    if (faceWrappers.length === 0) return;

    // Get category-specific images (no fallback - if not specified, images are hidden)
    const categoryHandle = category ? category.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') : null;
    const categoryImages: CategoryImages = (categoryHandle && window.categoryEngravingImages && window.categoryEngravingImages[categoryHandle]) 
      ? window.categoryEngravingImages[categoryHandle] 
      : {};

    // Inject images for each face
    faceWrappers.forEach((wrapper) => {
      const faceIndex = (wrapper as HTMLElement).dataset.faceIndex;
      const faceKey = `face${faceIndex}` as keyof CategoryImages;
      const faceImages: FaceImages = categoryImages[faceKey] || {};
      
      // Get image containers within this face wrapper
      const imageContainers = wrapper.querySelectorAll('.js-engraving-image');
      let hasAnyImage = false;

      imageContainers.forEach((container) => {
        const imageIndex = (container as HTMLElement).dataset.imageIndex;
        const imageKey = `image${imageIndex}` as keyof FaceImages;
        const imageSrc = faceImages[imageKey];

        if (imageSrc) {
          container.innerHTML = `<img src="${imageSrc}" alt="Face ${faceIndex} - Example ${imageIndex}" class="tw-block tw-object-contain tw-max-h-[65px]" loading="lazy">`;
          container.classList.remove('tw-hidden');
          hasAnyImage = true;
        } else {
          container.innerHTML = '';
          container.classList.add('tw-hidden');
        }
      });

      // Hide the entire wrapper if no images for this face
      if (hasAnyImage) {
        wrapper.classList.remove('tw-hidden');
      } else {
        wrapper.classList.add('tw-hidden');
      }
    });
  }

  private _init(): void {
    // Store bound handlers to avoid duplicates
    if (!this._boundHandlers) {
      this._boundHandlers = {
        updateStorage: this._updateStorageQuantity.bind(this),
        variantChange: this._handleVariantChange.bind(this),
        checkboxClick: this._handleCheckboxClick.bind(this),
        validateFields: this._validateEngravingFields.bind(this),
        buttonClick: this._handleButtonClick.bind(this)
      };
    }

    // Remove existing listeners before adding (to avoid duplicates)
    if (this.addButtonEl) {
      this.addButtonEl.removeEventListener('click', this._boundHandlers.buttonClick);
      this.addButtonEl.addEventListener('click', this._boundHandlers.buttonClick);
    }

    this.variantSwatches?.forEach(swatch => {
      swatch.removeEventListener('click', this._boundHandlers!.variantChange);
      swatch.addEventListener('click', this._boundHandlers!.variantChange);
    });

    this.faceCheckboxes?.forEach(checkbox => {
      checkbox.removeEventListener('click', this._boundHandlers!.checkboxClick);
      checkbox.addEventListener('click', this._boundHandlers!.checkboxClick);
    });

    // Add input listeners for engraving validation
    this._initEngravingInputListeners();

    this._loadFontOptions();
    
    // Subscribe to state changes to update prices when discount tier changes
    if (window.mixMatchStore && !this._unsubscribe) {
      this._unsubscribe = window.mixMatchStore.subscribe(({ action, state }) => {
        if (action === 'UPDATE_VARIANT_QUANTITY') {
          // Re-render prices with potentially new discount
          this._updatePriceDisplay(this.originalPrice);
        }
      });
    }
  }

  private _initEngravingInputListeners(): void {
    // Get all engraving inputs and add input event listeners
    const engravingInputs = this.content?.querySelectorAll('.js-engraving-input') || [];
    engravingInputs.forEach(input => {
      const inputEl = input as HTMLInputElement & { _engravingHandler?: () => void };
      
      // Create a handler that validates, updates char counter, and clears errors
      const inputHandler = (): void => {
        this._updateCharCounter(inputEl);
        this._clearInputError(inputEl);
        this._validateEngravingFields();
      };

      // Store handler reference for cleanup
      inputEl._engravingHandler = inputHandler;
      inputEl.removeEventListener('input', inputEl._engravingHandler);
      inputEl.addEventListener('input', inputHandler);
    });
  }

  private _updateCharCounter(input: HTMLInputElement): void {
    const maxLength = parseInt(input.dataset.maxLength || '15', 10);
    const currentLength = input.value.length;
    const remaining = maxLength - currentLength;

    // Find the counter element for this input
    const counterId = input.id;
    const counterEl = this.content?.querySelector(`.js-char-counter[data-for="${counterId}"]`);

    if (counterEl) {
      counterEl.textContent = String(remaining);
    }
  }

  private _resetCharCounters(): void {
    // Reset all character counters to their max values
    const counters = this.content?.querySelectorAll('.js-char-counter') || [];
    counters.forEach(counter => {
      const inputId = (counter as HTMLElement).dataset.for;
      const input = this.content?.querySelector(`#${inputId}`) as HTMLInputElement | null;
      const maxLength = input?.dataset.maxLength || '15';
      counter.textContent = maxLength;
    });
  }

  private _handleButtonClick(e: Event): void {
    // Check if form is valid
    const isValid = this._isFormValid();
    
    if (isValid) {
      // Form is valid, add to cart
      this._updateStorageQuantity();
    } else {
      // Form is invalid, show errors
      e.preventDefault();
      this._showValidationErrors();
    }
  }

  private _isFormValid(): boolean {
    if (!this.faceCheckboxes) return true;

    // Get all checked checkboxes
    const checkedCheckboxes = Array.from(this.faceCheckboxes).filter(cb => cb.checked);

    // If no checkboxes are checked, form is valid (add without engraving)
    if (checkedCheckboxes.length === 0) {
      return true;
    }

    // Check if all fields are filled for each checked checkbox
    for (const checkbox of checkedCheckboxes) {
      const checkboxId = checkbox.id;

      // Get the engraving inputs for this face
      const engraving1 = this.content?.querySelector(`#${checkboxId}-engraving-1`) as HTMLInputElement | null;
      const fontInput = this.content?.querySelector(`#${checkboxId}-font`) as HTMLInputElement | null;

      // Engraving 1 and font are required, engraving 2 is optional
      if (!engraving1?.value?.trim() || !fontInput?.value?.trim()) {
        return false;
      }
    }

    return true;
  }

  private _validateEngravingFields(): void {
    if (!this.faceCheckboxes || !this.addButtonEl) return;

    const isValid = this._isFormValid();
    
    // Update button visual state to look disabled when invalid
    if (isValid) {
      this.addButtonEl.classList.remove('is-invalid');
      this._clearAllErrors();
    } else {
      this.addButtonEl.classList.add('is-invalid');
    }
  }

  private _showValidationErrors(): void {
    if (!this.faceCheckboxes) return;

    // Get all checked checkboxes
    const checkedCheckboxes = Array.from(this.faceCheckboxes).filter(cb => cb.checked);

    checkedCheckboxes.forEach(checkbox => {
      const checkboxId = checkbox.id;

      // Get the engraving inputs and font input for this face
      const engraving1 = this.content?.querySelector(`#${checkboxId}-engraving-1`) as HTMLInputElement | null;
      const fontInput = this.content?.querySelector(`#${checkboxId}-font`) as HTMLInputElement | null;

      // Check engraving 1 (required)
      if (engraving1 && !engraving1.value?.trim()) {
        this._showInputError(engraving1);
      }

      // Check font select (required)
      if (fontInput && !fontInput.value?.trim()) {
        this._showSelectError(fontInput);
      }
    });
  }

  private _showInputError(input: HTMLInputElement): void {
    if (!input) return;
    
    // Add red border to input
    input.classList.add('tw-border', 'tw-border-flame-red', 'tw-border-solid', '!tw-bg-flame-red/10');
    
    // Show error message
    const errorEl = this.content?.querySelector(`.js-input-error[data-for="${input.id}"]`);
    if (errorEl) {
      errorEl.classList.remove('tw-hidden');
    }
  }

  private _showSelectError(hiddenInput: HTMLInputElement): void {
    if (!hiddenInput) return;
    
    // Find the custom select container
    const customSelect = hiddenInput.closest('.js-custom-select');
    if (customSelect) {
      // Add red border to trigger button
      const trigger = customSelect.querySelector('.js-custom-select-trigger');
      if (trigger) {
        trigger.classList.add('tw-border', 'tw-border-flame-red', 'tw-border-solid', '!tw-bg-flame-red/10');
      }
    }
    
    // Show error message
    const errorEl = this.content?.querySelector(`.js-select-error[data-for="${hiddenInput.id}"]`);
    if (errorEl) {
      errorEl.classList.remove('tw-hidden');
    }
  }

  private _clearInputError(input: HTMLInputElement): void {
    if (!input) return;
    
    // Remove red border from input
    input.classList.remove('tw-border', 'tw-border-flame-red', 'tw-border-solid', '!tw-bg-flame-red/10');
    
    // Hide error message
    const errorEl = this.content?.querySelector(`.js-input-error[data-for="${input.id}"]`);
    if (errorEl) {
      errorEl.classList.add('tw-hidden');
    }
  }

  private _clearSelectError(hiddenInput: HTMLInputElement): void {
    if (!hiddenInput) return;
    
    // Find the custom select container
    const customSelect = hiddenInput.closest('.js-custom-select');
    if (customSelect) {
      // Remove red border from trigger button
      const trigger = customSelect.querySelector('.js-custom-select-trigger');
      if (trigger) {
        trigger.classList.remove('tw-border', 'tw-border-flame-red', 'tw-border-solid', '!tw-bg-flame-red/10');
      }
    }
    
    // Hide error message
    const errorEl = this.content?.querySelector(`.js-select-error[data-for="${hiddenInput.id}"]`);
    if (errorEl) {
      errorEl.classList.add('tw-hidden');
    }
  }

  private _clearAllErrors(): void {
    // Clear all input errors
    const inputs = this.content?.querySelectorAll('.js-engraving-input') || [];
    inputs.forEach(input => this._clearInputError(input as HTMLInputElement));
    
    // Clear all select errors
    const hiddenInputs = this.content?.querySelectorAll('.js-custom-select input[type="hidden"]') || [];
    hiddenInputs.forEach(input => this._clearSelectError(input as HTMLInputElement));
  }

  private _cacheUIElements(): void {
    const contentEl = this.content;
    if (!contentEl) {
      return;
    }

    this.addButtonEl = contentEl.querySelector('.js-popup-add');
    this.priceEls = contentEl.querySelectorAll('.js-price');
    this.mainContent = contentEl.querySelector('[data-main]');
    this.variantSwatches = contentEl.querySelectorAll('[data-varaint-swatch]');
    this.faceCheckboxes = contentEl.querySelectorAll('.js-face-checkbox');
    this.customSelects = contentEl.querySelectorAll('.js-custom-select');
    this.productGallery = contentEl.querySelector('product-gallery');
    this.colorNameEl = contentEl.querySelector('[data-color-name]');
    this.productTitleEl = contentEl.querySelector('[data-product-title]');
    this.titlePriceEl = contentEl.querySelector('.js-title-price');
    this.buttonPriceEl = contentEl.querySelector('.js-button-price');
  }

  private _fillData(): void {
    const scriptEl = this.querySelector('script[type="application/json"]');
    if (scriptEl?.textContent) {
      this.foxsellVariants = JSON.parse(scriptEl.textContent)
        .filter((variant: FoxsellVariant) => variant.id != undefined);
      
      // Store original Shopify prices before overriding (needed for surcharge calculation)
      this.shopifyPrices = {};
      this.foxsellVariants.forEach(variant => {
        this.shopifyPrices[variant.id] = variant.price;
      });
      
      // Override prices with global Foxsell price map if available
      if (window.foxsellPriceMap) {
        this.foxsellVariants = this.foxsellVariants.map(variant => {
          const foxsellPrice = window.foxsellPriceMap![String(variant.id)];
          if (foxsellPrice !== undefined) {
            return { ...variant, price: foxsellPrice };
          }
          return variant;
        });
      }
    } else {
      this.foxsellVariants = [];
      this.shopifyPrices = {};
    }

    if (this.mainContent) {
      this.variantId = this.mainContent.dataset.mixProduct ? Number(this.mainContent.dataset.mixProduct) : '';
      this.title = this.mainContent.dataset.title || '';
      
      // Use global Foxsell price map for correct price, fallback to data attribute
      const foxsellPrice = window.foxsellPriceMap?.[String(this.variantId)];
      this.originalPrice = foxsellPrice !== undefined ? foxsellPrice : Number(this.mainContent.dataset.price);
      
      this.handle = this.mainContent.dataset.handle || '';
      this.productType = this.mainContent.dataset.type || '';
      this.image = this.mainContent.dataset.image || '';
      this.inventoryQuantity = parseInt(this.mainContent.dataset.inventoryQuantity || '0', 10);
      this.inventoryManagement = this.mainContent.dataset.inventoryManagement || '';
      this.inventoryPolicy = this.mainContent.dataset.inventoryPolicy || '';
    }

    // Store base variant info for reverting when no face is selected
    this.baseVariantId = this.variantId;
    this.baseTitle = this.title;
    this.basePrice = this.originalPrice;
    this.baseHandle = this.handle;

    // Get category from trigger element if available
    this.category = this._lastTriggerEl?.dataset?.category || '';

    // Update displayed price with correct Foxsell price
    this._updatePriceDisplay(this.originalPrice);

    // Sync variant picker to match the current product
    this._syncVariantPicker();
  }

  private _syncVariantPicker(): void {
    if (!this.variantSwatches?.length || !this.variantId) return;

    // Find the swatch that matches the current variantId
    let matchingSwatch: HTMLInputElement | null = null;
    this.variantSwatches.forEach(swatch => {
      const swatchVariantId = parseInt(swatch.dataset.variantId || '0', 10);
      if (swatchVariantId === this.variantId) {
        matchingSwatch = swatch;
      }
    });

    if (matchingSwatch !== null) {
      const swatch = matchingSwatch as HTMLInputElement;
      
      // Uncheck all swatches and check the matching one
      this.variantSwatches.forEach(s => {
        s.checked = false;
      });
      swatch.checked = true;

      // Update gallery to show the correct product's media
      const productMedia = swatch.dataset.productMedia;
      const productColor = swatch.dataset.productColor;
      const productTitle = swatch.dataset.productTitle;
      const productImage = swatch.dataset.productImage;

      if (productMedia && this.productGallery) {
        try {
          const mediaArray = JSON.parse(productMedia);
          this.productGallery.dispatchEvent(new CustomEvent('product:changed', {
            detail: { media: mediaArray },
            bubbles: true
          }));
        } catch (e) {
          console.error('Failed to parse product media:', e);
        }
      }

      // Update product title display
      if (this.productTitleEl && productTitle) {
        this.productTitleEl.textContent = productTitle;
      }

      // Update color name display
      if (this.colorNameEl && productColor) {
        this.colorNameEl.textContent = productColor;
      }

      // Update product image for cart
      if (productImage) {
        this.image = productImage;
      }

      // Update face checkbox variant IDs for the current product
      const faceVariantsData = swatch.dataset.faceVariants;
      if (faceVariantsData) {
        try {
          const faceVariants: FaceVariant[] = JSON.parse(faceVariantsData);
          this._updateFaceCheckboxVariants(faceVariants);
        } catch (e) {
          console.error('Failed to parse face variants:', e);
        }
      }
    }
  }

  private _handleVariantChange(event: Event): void {
    event.stopPropagation();

    const target = event.target as HTMLInputElement;

    this.variantSwatches?.forEach(swatch => {
      if (swatch !== target) swatch.checked = false;
    });

    const targetVariantId = parseInt(target.dataset.variantId || '0', 10);
    const productMedia = target.dataset.productMedia;
    const productColor = target.dataset.productColor;
    const productTitle = target.dataset.productTitle;
    const productImage = target.dataset.productImage;

    // Dispatch product:changed event to product-gallery
    if (productMedia && this.productGallery) {
      try {
        const mediaArray = JSON.parse(productMedia);
        this.productGallery.dispatchEvent(new CustomEvent('product:changed', {
          detail: { media: mediaArray },
          bubbles: true
        }));
      } catch (e) {
        console.error('Failed to parse product media:', e);
      }
    }

    // Update product title display
    if (this.productTitleEl && productTitle) {
      this.productTitleEl.textContent = productTitle;
    }

    // Update color name display
    if (this.colorNameEl && productColor) {
      this.colorNameEl.textContent = productColor;
    }

    // Update product image for cart
    if (productImage) {
      this.image = productImage;
    }

    const matchingVariant = this.foxsellVariants.find((variant) => {
      return variant.id === targetVariantId;
    });

    if (matchingVariant) {
      this.switchToVariant(matchingVariant);

      // Update BASE variant when switching products (so unchecking face reverts to correct product)
      this.baseVariantId = matchingVariant.id;
      this.baseTitle = matchingVariant.title;
      // Use the Foxsell price (already set in switchToVariant) for basePrice
      this.basePrice = this.originalPrice;
      this.baseHandle = matchingVariant.handle || '';

      // Update face checkbox variant IDs for the new product
      const faceVariantsData = target.dataset.faceVariants;
      if (faceVariantsData) {
        try {
          const faceVariants: FaceVariant[] = JSON.parse(faceVariantsData);
          this._updateFaceCheckboxVariants(faceVariants);
        } catch (e) {
          console.error('Failed to parse face variants:', e);
        }
      }

      // Reset face checkboxes when switching products
      this._clearEngravingInputs();
    }
  }

  private _updateFaceCheckboxVariants(faceVariants: FaceVariant[]): void {
    if (!this.faceCheckboxes || !faceVariants) return;

    // Sort checkboxes by face index
    const sortedCheckboxes = Array.from(this.faceCheckboxes).sort(
      (a, b) => parseInt(a.dataset.faceIndex || '0', 10) - parseInt(b.dataset.faceIndex || '0', 10)
    );

    // Update each checkbox's variant ID with the corresponding face variant
    sortedCheckboxes.forEach((checkbox, index) => {
      const faceVariant = faceVariants.find(v => v.index === index);
      if (faceVariant) {
        checkbox.dataset.variantId = String(faceVariant.id);
      }
    });
  }

  private _handleCheckboxClick(event: Event): void {
    const clickedCheckbox = event.target as HTMLInputElement;
    const faceIndex = parseInt(clickedCheckbox.dataset.faceIndex || '0', 10);
    const isChecked = clickedCheckbox.checked;

    // Get all checkboxes as array sorted by face index
    const checkboxes = Array.from(this.faceCheckboxes || []).sort(
      (a, b) => parseInt(a.dataset.faceIndex || '0', 10) - parseInt(b.dataset.faceIndex || '0', 10)
    );

    // Handle first checkbox (index 0) logic
    if (faceIndex === 0) {
      const secondCheckbox = checkboxes[1];

      if (secondCheckbox) {
        if (isChecked) {
          // Enable second checkbox when first is checked
          secondCheckbox.disabled = false;
        } else {
          // Disable and uncheck second checkbox when first is unchecked
          secondCheckbox.checked = false;
          secondCheckbox.disabled = true;
          // Clear second checkbox's inputs (skip validation, we'll do it after)
          this._clearInputsForCheckbox(secondCheckbox, true);
        }
      }
    }

    // Clear inputs if checkbox is unchecked (skip validation, we'll do it after)
    if (!isChecked) {
      this._clearInputsForCheckbox(clickedCheckbox, true);
    }

    // Validate engraving fields and update button state
    this._validateEngravingFields();

    // Find the last checked checkbox to determine variant
    const selectedCheckbox = checkboxes.filter(checkbox => checkbox.checked).at(-1);

    if (selectedCheckbox) {
      const targetVariantId = parseInt(selectedCheckbox.dataset.variantId || '0', 10);
      const matchingVariant = this.foxsellVariants.find((variant) => variant.id === targetVariantId);

      if (matchingVariant) {
        this.switchToVariant(matchingVariant);
      }
    } else {
      // No checkbox selected - revert to base product variant
      this.variantId = this.baseVariantId;
      this.title = this.baseTitle;
      this.originalPrice = this.basePrice;
      this.handle = this.baseHandle;

      // Update displayed price to base price
      this._updatePriceDisplay(this.basePrice);
    }
  }

  private _clearInputsForCheckbox(checkbox: HTMLInputElement, skipValidation: boolean = false): void {
    // Clear text inputs and reset their counters
    const targetInputs = this.querySelectorAll(`input[data-input-type="${checkbox.id}"]`);
    targetInputs.forEach(input => {
      const inputEl = input as HTMLInputElement;
      inputEl.value = '';
      // Reset character counter and font-family for engraving inputs
      if (inputEl.classList.contains('js-engraving-input')) {
        this._updateCharCounter(inputEl);
        inputEl.style.fontFamily = '';
      }
    });

    // Reset custom select elements
    const targetCustomSelects = this.querySelectorAll(`.js-custom-select[data-input-type="${checkbox.id}"]`);
    targetCustomSelects.forEach(customSelect => {
      const hiddenInput = customSelect.querySelector('input[type="hidden"]') as HTMLInputElement | null;
      const label = customSelect.querySelector('.js-custom-select-label') as HTMLElement | null;
      const optionsList = customSelect.querySelector('.js-custom-select-options');

      // Reset hidden input
      if (hiddenInput) hiddenInput.value = '';

      // Reset label to placeholder
      if (label) {
        label.textContent = (customSelect as HTMLElement).dataset.placeholder || 'Choisir';
        label.style.fontFamily = '';
        label.classList.add('tw-text-auroMetalSaurus');
        label.classList.remove('tw-text-maastrichtBlue');
      }

      // Remove selected state from options
      if (optionsList) {
        optionsList.querySelectorAll('li').forEach(item => {
          item.classList.remove('tw-bg-cultured', 'tw-font-medium');
        });
      }
    });

    // Re-validate after clearing (unless skipped to avoid duplicate calls)
    if (!skipValidation) {
      this._validateEngravingFields();
    }
  }

  private _loadFontOptions(): void {
    this.customSelects?.forEach(customSelect => {
      const csvUrl = (customSelect as HTMLElement).dataset.csvUrl;
      if (!csvUrl) return;

      const optionsList = customSelect.querySelector('.js-custom-select-options') as HTMLElement | null;
      const trigger = customSelect.querySelector('.js-custom-select-trigger') as HTMLElement | null;
      const dropdown = customSelect.querySelector('.js-custom-select-dropdown') as HTMLElement | null;
      const label = customSelect.querySelector('.js-custom-select-label') as HTMLElement | null;
      const arrow = customSelect.querySelector('.js-custom-select-arrow') as HTMLElement | null;
      const hiddenInput = customSelect.querySelector('input[type="hidden"]') as HTMLInputElement | null;

      if (!optionsList || !trigger || !dropdown || !label || !arrow || !hiddenInput) return;

      // Check if options already loaded
      if (optionsList.children.length > 0) return;

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = !dropdown.classList.contains('tw-hidden');

        // Close all other dropdowns first
        this.customSelects?.forEach(other => {
          other.querySelector('.js-custom-select-dropdown')?.classList.add('tw-hidden');
          other.querySelector('.js-custom-select-arrow')?.classList.remove('tw-rotate-180');
        });

        if (!isOpen) {
          // Show dropdown (positioned absolutely within its container)
          dropdown.classList.remove('tw-hidden');
          arrow.classList.add('tw-rotate-180');
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!customSelect.contains(e.target as Node)) {
          dropdown.classList.add('tw-hidden');
          arrow.classList.remove('tw-rotate-180');
        }
      });

      fetch(csvUrl)
        .then(response => response.text())
        .then(csvData => {
          const fonts: FontOption[] = [];

          // Parse CSV
          if (typeof window.Papa !== 'undefined') {
            window.Papa.parse(csvData, {
              header: true,
              complete: (results) => {
                results.data.forEach(font => {
                  if (font.font_name) {
                    fonts.push({ name: font.font_name, url: font.font_url });
                  }
                });
                this._populateCustomSelect(optionsList, fonts, { label, hiddenInput, dropdown, arrow });
              }
            });
          } else {
            // Simple CSV fallback
            const lines = csvData.trim().split('\n');
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            const fontNameIndex = headers.indexOf('font_name');
            const fontUrlIndex = headers.indexOf('font_url');

            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(',');
              const fontName = values[fontNameIndex]?.trim();
              const fontUrl = fontUrlIndex >= 0 ? values[fontUrlIndex]?.trim() : null;
              if (fontName) {
                fonts.push({ name: fontName, url: fontUrl });
              }
            }
            this._populateCustomSelect(optionsList, fonts, { label, hiddenInput, dropdown, arrow });
          }
        })
        .catch(err => console.error('Failed to load font options:', err));
    });
  }

  private _populateCustomSelect(optionsList: HTMLElement, fonts: FontOption[], elements: CustomSelectElements): void {
    const { label, hiddenInput, dropdown, arrow } = elements;

    fonts.forEach(font => {
      // Load font stylesheet if URL provided
      if (font.url) {
        const existingLink = document.querySelector(`link[href="${font.url}"]`);
        if (!existingLink) {
          const link = document.createElement('link');
          link.href = font.url;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      }

      // Create option element
      const li = document.createElement('li');
      li.className = 'tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-cultured tw-transition-colors tw-text-sm';
      li.dataset.value = font.name;
      li.textContent = font.name;
      li.style.fontFamily = `"${font.name}", sans-serif`;

      // Handle option click
      li.addEventListener('click', () => {
        // Update hidden input value
        hiddenInput.value = font.name;

        // Update label text and font
        label.textContent = font.name;
        label.style.fontFamily = `"${font.name}", sans-serif`;
        label.classList.remove('tw-text-auroMetalSaurus');
        label.classList.add('tw-text-maastrichtBlue');

        // Close dropdown
        dropdown.classList.add('tw-hidden');
        arrow.classList.remove('tw-rotate-180');

        // Mark selected option
        optionsList.querySelectorAll('li').forEach(item => {
          item.classList.remove('tw-bg-cultured', 'tw-font-medium');
        });
        li.classList.add('tw-bg-cultured', 'tw-font-medium');

        // Clear select error when font is selected
        this._clearSelectError(hiddenInput);

        // Update engraving input fields with selected font
        const inputType = optionsList.closest('.js-custom-select')?.getAttribute('data-input-type');
        if (inputType) {
          const engraving1 = this.content?.querySelector(`#${inputType}-engraving-1`) as HTMLInputElement | null;
          const engraving2 = this.content?.querySelector(`#${inputType}-engraving-2`) as HTMLInputElement | null;
          if (engraving1) engraving1.style.fontFamily = `"${font.name}", sans-serif`;
          if (engraving2) engraving2.style.fontFamily = `"${font.name}", sans-serif`;
        }

        // Validate engraving fields after font selection
        this._validateEngravingFields();
      });

      optionsList.appendChild(li);
    });
  }

  switchToVariant(matchingVariant: FoxsellVariant): void {
    this.variantId = matchingVariant.id;
    this.title = matchingVariant.title;
    // Preserve base handle if variant doesn't have one (common for face/personalization variants)
    this.handle = matchingVariant.handle || this.baseHandle || '';
    
    // Use global Foxsell price map for correct price
    const foxsellPrice = window.foxsellPriceMap?.[String(matchingVariant.id)];
    
    if (foxsellPrice !== undefined) {
      // Foxsell price found for this exact variant
      this.originalPrice = foxsellPrice;
    } else {
      // Foxsell price not found - this might be a face/personalization variant
      // Calculate as: base Foxsell price + customization surcharge
      // The surcharge is (face variant Shopify price - base variant Shopify price)
      
      // Get the base variant's Foxsell price
      const baseFoxsellPrice = window.foxsellPriceMap?.[String(this.baseVariantId)];
      
      if (baseFoxsellPrice !== undefined && this.shopifyPrices) {
        // Get original Shopify prices (before Foxsell override)
        const baseShopifyPrice = this.shopifyPrices[this.baseVariantId] || 0;
        const faceShopifyPrice = this.shopifyPrices[matchingVariant.id] || matchingVariant.price || 0;
        
        // Calculate customization surcharge (difference between face and base Shopify prices)
        const customizationSurcharge = faceShopifyPrice - baseShopifyPrice;
        
        // Final price = Foxsell base price + surcharge
        this.originalPrice = baseFoxsellPrice + customizationSurcharge;
      } else {
        // Fallback to variant price from JSON
        this.originalPrice = matchingVariant.price;
      }
    }

    // Update displayed price
    this._updatePriceDisplay(this.originalPrice);
  }

  private _updatePriceDisplay(price: number): void {
    // Get current bundle discount from store
    const currentDiscount = window.mixMatchStore?.state?.currentDiscount || 0;
    
    // Calculate discounted price
    const discountedPrice = Math.ceil(price * (1 - currentDiscount / 100));
    
    // Format prices using Shopify's money format
    const formatPrice = (p: number) => window.formatMoney ? window.formatMoney(p, window.moneyFormat || '') : `${(p / 100).toFixed(2)} €`;
    const formattedDiscountedPrice = formatPrice(discountedPrice);
    const formattedOriginalPrice = formatPrice(price);

    // Update price under title
    if (this.titlePriceEl) {
      if (currentDiscount > 0) {
        // Show discounted price in red with original price strikethrough
        this.titlePriceEl.innerHTML = `<span class="tw-text-flame-red">${formattedDiscountedPrice}</span> <span class="tw-line-through tw-text-sm tw-text-auroMetalSaurus tw-ml-1">${formattedOriginalPrice}</span>`;
      } else {
        this.titlePriceEl.textContent = formattedOriginalPrice;
      }
    }

    // Update button price with discount display
    if (this.buttonPriceEl) {
      if (currentDiscount > 0) {
        // Show discounted price with original price strikethrough
        this.buttonPriceEl.innerHTML = `<span>${formattedDiscountedPrice}</span> <span class="tw-line-through tw-text-xs tw-opacity-70">${formattedOriginalPrice}</span>`;
      } else {
        this.buttonPriceEl.textContent = formattedOriginalPrice;
      }
    }
  }

  private _updateStorageQuantity(): void {
    // Calculate available inventory (respect inventory settings)
    let maxInventory = this.inventoryQuantity;
    if (this.inventoryManagement !== 'shopify' || this.inventoryPolicy === 'continue') {
      maxInventory = 999;
    }

    // Gather engraving properties from checked face checkboxes
    const properties = this._getEngravingProperties();

    const storageData: StorageData = {
      id: this.variantId,
      category: this.category,
      quantity: 1,
      title: this.title,
      image: this.image,
      price: this.originalPrice,
      type: "product",
      handle: this.handle,
      inventory_quantity: maxInventory,
      product_type: this.productType,
      properties,
    };

    window.mixMatchStore.setStorageData(storageData);

    // Reset to first product and close popup
    this._resetAndClose();
  }

  private _resetAndClose(): void {
    // Clear engraving inputs
    this._clearEngravingInputs();

    // Reset picker to first swatch
    if (this.variantSwatches?.length && this.variantSwatches.length > 0) {
      const firstSwatch = this.variantSwatches[0];

      // Uncheck all swatches and check first one
      this.variantSwatches.forEach(swatch => {
        swatch.checked = false;
      });
      firstSwatch.checked = true;

      // Reset gallery to first product's media
      const productMedia = firstSwatch.dataset.productMedia;
      const productColor = firstSwatch.dataset.productColor;
      const productTitle = firstSwatch.dataset.productTitle;
      const productImage = firstSwatch.dataset.productImage;

      if (productMedia && this.productGallery) {
        try {
          const mediaArray = JSON.parse(productMedia);
          this.productGallery.dispatchEvent(new CustomEvent('product:changed', {
            detail: { media: mediaArray },
            bubbles: true
          }));
        } catch (e) {
          console.error('Failed to parse product media:', e);
        }
      }

      // Update product title display
      if (this.productTitleEl && productTitle) {
        this.productTitleEl.textContent = productTitle;
      }

      // Update color name display
      if (this.colorNameEl && productColor) {
        this.colorNameEl.textContent = productColor;
      }

      // Update product image for cart
      if (productImage) {
        this.image = productImage;
      }

      // Reset variant to first product
      const firstVariantId = parseInt(firstSwatch.dataset.variantId || '0', 10);
      const firstVariant = this.foxsellVariants.find(v => v.id === firstVariantId);

      if (firstVariant) {
        this.switchToVariant(firstVariant);
        this.baseVariantId = firstVariant.id;
        this.baseTitle = firstVariant.title;
        // Use the Foxsell price (already set in switchToVariant) for basePrice
        this.basePrice = this.originalPrice;

        // Update face checkbox variant IDs for the first product
        const faceVariantsData = firstSwatch.dataset.faceVariants;
        if (faceVariantsData) {
          try {
            const faceVariants: FaceVariant[] = JSON.parse(faceVariantsData);
            this._updateFaceCheckboxVariants(faceVariants);
          } catch (e) {
            console.error('Failed to parse face variants:', e);
          }
        }
        this.baseHandle = firstVariant.handle || '';
      }
    }

    // Close the popup (close() will handle re-fetch check)
    this.close();
  }

  private _getEngravingProperties(): EngravingProperties {
    const properties: EngravingProperties = {};

    if (!this.faceCheckboxes) return properties;

    // Get all checked face checkboxes sorted by index
    const checkedCheckboxes = Array.from(this.faceCheckboxes)
      .filter(checkbox => checkbox.checked)
      .sort((a, b) => parseInt(a.dataset.faceIndex || '0', 10) - parseInt(b.dataset.faceIndex || '0', 10));

    checkedCheckboxes.forEach((checkbox) => {
      const faceIndex = parseInt(checkbox.dataset.faceIndex || '0', 10) + 1; // 1-based for display
      const checkboxId = checkbox.id;

      // Get engraving inputs for this face
      const engraving1Input = this.content?.querySelector(`#${checkboxId}-engraving-1`) as HTMLInputElement | null;
      const engraving2Input = this.content?.querySelector(`#${checkboxId}-engraving-2`) as HTMLInputElement | null;

      // Get font select (custom select hidden input)
      const fontInput = this.content?.querySelector(`#${checkboxId}-font`) as HTMLInputElement | null;

      if (engraving1Input?.value) {
        properties[`face${faceIndex}-engraving1`] = engraving1Input.value;
      }
      if (engraving2Input?.value) {
        properties[`face${faceIndex}-engraving2`] = engraving2Input.value;
      }
      if (fontInput?.value) {
        properties[`face${faceIndex}-fontStyle`] = fontInput.value;
      }
    });

    return properties;
  }

  private _clearEngravingInputs(): void {
    if (!this.faceCheckboxes) return;

    this.faceCheckboxes.forEach((checkbox) => {
      const checkboxId = checkbox.id;

      // Clear engraving inputs
      const engraving1Input = this.content?.querySelector(`#${checkboxId}-engraving-1`) as HTMLInputElement | null;
      const engraving2Input = this.content?.querySelector(`#${checkboxId}-engraving-2`) as HTMLInputElement | null;
      const fontInput = this.content?.querySelector(`#${checkboxId}-font`) as HTMLInputElement | null;

      if (engraving1Input) {
        engraving1Input.value = '';
        engraving1Input.style.fontFamily = '';
      }
      if (engraving2Input) {
        engraving2Input.value = '';
        engraving2Input.style.fontFamily = '';
      }
      if (fontInput) fontInput.value = '';

      // Reset custom select label if exists
      const customSelect = fontInput?.closest('.js-custom-select');
      if (customSelect) {
        const label = customSelect.querySelector('.js-custom-select-label') as HTMLElement | null;
        const placeholder = (customSelect as HTMLElement).dataset.placeholder || 'Choisir';
        if (label) {
          label.textContent = placeholder;
          label.style.fontFamily = '';
          label.classList.add('tw-text-auroMetalSaurus');
          label.classList.remove('tw-text-maastrichtBlue');
        }
        // Remove selected state from options
        const optionsList = customSelect.querySelector('.js-custom-select-options');
        if (optionsList) {
          optionsList.querySelectorAll('li').forEach(item => {
            item.classList.remove('tw-bg-cultured', 'tw-font-medium');
          });
        }
      }

      // Uncheck checkbox and reset accordion
      checkbox.checked = false;
    });

    // Reset second checkbox to disabled state
    const checkboxes = Array.from(this.faceCheckboxes).sort(
      (a, b) => parseInt(a.dataset.faceIndex || '0', 10) - parseInt(b.dataset.faceIndex || '0', 10)
    );
    if (checkboxes[1]) {
      checkboxes[1].disabled = true;
    }

    // Reset character counters
    this._resetCharCounters();

    // Re-validate to enable button (no checkboxes checked)
    this._validateEngravingFields();
  }
}

customElements.define("mm-product-pop-up", MMProductPopup);

